// Where you are headed: a spot clicked on the map, a landmark, a friend, or
// one of the map's marks, wherever it is on the grid.
//
// One thing at a time, as the desktop viewer has it. It belongs to the
// viewer rather than to the map window, so closing the map or teleporting
// leaves it standing; it is put down by arriving, by clicking the arrow
// that points at it, or by being asked to stop.
//
// Everything here is arithmetic on grid squares and metres. The beam, the
// label and the arrow are drawn elsewhere from what this works out, so it
// can be checked without a screen.

import type { Vec3 } from "@/net/codec";
import { FriendPresence, type FriendLocateReply } from "@/net/messages";
import type { RegionConnection } from "@/net/region";
import type { World } from "./world";

/**
 * What is being tracked. A location is a bare spot; an event or land is
 * one of the map's marks, which the map rings more widely so the mark
 * itself still shows.
 */
export type TrackKind = "location" | "landmark" | "friend" | "event" | "land";

export interface TrackTarget {
  kind: TrackKind;
  regionId: string;
  regionName: string;
  /** The region's square on the grid. */
  gridX: number;
  gridY: number;
  /** Where in the region, in its own metres. */
  position: Vec3;
  /** What the beacon and the map call it, and a second line under that. */
  label: string;
  detail: string;
  /** The friend followed, the landmark's asset or the map mark; empty for a bare spot. */
  id: string;
}

/** The region stood in, as far as finding anything else goes. */
export interface Here {
  regionId: string;
  gridX: number;
  gridY: number;
  /** Metres across a grid square, which is how far apart regions sit. */
  size: number;
  /** Where you are, in the region's own metres. */
  self: Vec3;
  /** The ground under a point of this region, when it is known. */
  ground?: (x: number, y: number) => number;
}

/** The region a world is of, and where you stand in it, for finding things from. */
export function hereIn(world: World): Here | null {
  const self = world.self;
  if (!self) return null;
  const info = world.info;
  return {
    regionId: info.region_id,
    gridX: info.grid_x,
    gridY: info.grid_y,
    size: info.size_x || 256,
    self: self.position,
    ground: (x, y) => world.terrain.heightAt(x, y),
  };
}

/** Arriving within this many metres puts the beacon down. */
export const ARRIVED_WITHIN = 3;
/**
 * A landmark tracked from closer than this is not arrived at until it has
 * been left and come back to: otherwise tracking the landmark you are
 * standing on would end before anything could be seen of it.
 */
export const LANDMARK_NEAR = 12;
/** How often a friend being followed is looked for again. */
export const FRIEND_POLL_MS = 10_000;
/** A spot is never shown below this far above the ground. */
const ABOVE_GROUND = 1.5;

/**
 * Where a target is in the metres of the region stood in.
 *
 * Regions sit a grid square apart, so a target's place is its own metres
 * plus however many squares its region is from this one. Worked out from
 * the two squares each time rather than kept as a running offset, which
 * would be right only until the next step across a border.
 */
export function relativeTo(here: Here, t: TrackTarget): Vec3 {
  return {
    x: t.position.x + (t.gridX - here.gridX) * here.size,
    y: t.position.y + (t.gridY - here.gridY) * here.size,
    z: t.position.z,
  };
}

/**
 * How far from arriving you are.
 *
 * A friend moves about, so only the ground between counts. Anywhere else
 * the height counts for half: a spot on the map is a place more than a
 * point in the air, and is reached a little sooner from above or below.
 */
export function arrivalGap(kind: TrackKind, self: Vec3, spot: Vec3): number {
  const dx = spot.x - self.x;
  const dy = spot.y - self.y;
  if (kind === "friend") return Math.hypot(dx, dy);
  return Math.hypot(dx, dy, (spot.z - self.z) * 0.5);
}

/** A spot named the way the map names one: "Region (x, y, z)". */
export function placeLabel(regionName: string, p: Vec3): string {
  return `${regionName} (${Math.round(p.x)}, ${Math.round(p.y)}, ${Math.round(p.z)})`;
}

/** A link that opens the map on a spot: `origin/?place=Region/x/y/z`. */
export function placeLink(origin: string, regionName: string, p: Vec3): string {
  const where = [Math.round(p.x), Math.round(p.y), Math.round(p.z)].join("/");
  return `${origin}/?place=${encodeURIComponent(regionName)}/${where}`;
}

const NUMBER = /^-?\d+(\.\d+)?$/;

/**
 * Read a place link's `Region/x/y/z` back.
 *
 * The coordinates are taken from the end, as many as were given, so a
 * name with a slash in it survives; whatever is missing is the middle of
 * the region at ground level, as the desktop viewer assumes.
 */
export function parsePlace(text: string): { regionName: string; position: Vec3 } | null {
  const parts = text.split("/").map((p) => p.trim()).filter(Boolean);
  let split = parts.length;
  while (split > 1 && parts.length - split < 3 && NUMBER.test(parts[split - 1])) split--;
  const regionName = parts.slice(0, split).join("/");
  if (!regionName) return null;
  const [x = 128, y = 128, z = 0] = parts.slice(split).map(Number);
  return { regionName, position: { x: Math.max(0, x), y: Math.max(0, y), z: Math.max(0, z) } };
}

/**
 * A point pushed out along the line from the middle of a box until it
 * meets the edge, when it is not already inside. Measured from the middle;
 * `halfW` and `halfH` are half the box's size.
 */
export function toEdge(x: number, y: number, halfW: number, halfH: number): { x: number; y: number; inside: boolean } {
  if (Math.abs(x) <= halfW && Math.abs(y) <= halfH) return { x, y, inside: true };
  const k = Math.min(halfW / Math.max(1e-9, Math.abs(x)), halfH / Math.max(1e-9, Math.abs(y)));
  return { x: x * k, y: y * k, inside: false };
}

/**
 * Where the arrow on the screen that points at a tracked target goes.
 *
 * `x`, `y` is the target's place on the screen measured from the middle,
 * y down. For a target behind the camera it is only a direction, and is
 * pushed out to the edge like one beside the view. The arrow stands off
 * from the target towards the middle, twice as far across as up and down
 * so it clears a label, stays inside the window, and points from the
 * middle at the target. Nothing is drawn for a target dead ahead.
 */
export function hudArrow(
  x: number,
  y: number,
  behind: boolean,
  width: number,
  height: number,
  size: number,
): { x: number; y: number; angle: number } | null {
  const halfW = width / 2;
  const halfH = height / 2;
  let at = { x, y };
  if (behind) {
    // Straight behind has no direction on the screen; down is as good as any.
    if (Math.hypot(x, y) < 1e-6) at = { x: 0, y: 1 };
    at = toEdge(at.x * 1e6, at.y * 1e6, halfW, halfH);
  } else {
    at = toEdge(x, y, halfW, halfH);
  }
  const d = Math.hypot(at.x, at.y);
  if (d < 1e-3) return null;
  const ux = at.x / d;
  const uy = at.y / d;
  const back = toEdge(at.x - 2 * size * ux, at.y - size * uy, halfW - size / 2, halfH - size / 2);
  return { x: halfW + back.x, y: halfH + back.y, angle: Math.atan2(uy, ux) };
}

/**
 * The one thing being tracked, and the friend being followed when that is
 * what it is.
 *
 * A friend is not anywhere until the grid says where: following one asks
 * at once and every ten seconds after, and the target appears with the
 * first answer. Saying they cannot be found, or going offline, ends it.
 */
export class Tracker {
  private current: TrackTarget | null = null;
  private friend: { id: string; name: string } | null = null;
  private askedAt = 0;
  /** For a landmark: whether arriving counts yet. */
  private armed = true;
  /** Whether the first look at how far away it is has been taken. */
  private measured = false;
  private listeners = new Set<() => void>();
  /** Ask the region where a friend is. */
  locate: ((id: string) => void) | null = null;
  /** Say something about the tracking in chat. */
  notice: ((text: string) => void) | null = null;

  get target(): TrackTarget | null {
    return this.current;
  }

  /** The friend being followed, found yet or not. */
  get following(): string | null {
    return this.friend?.id ?? null;
  }

  /** Hear about every change: a new target, a moved one, or none. */
  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  track(t: TrackTarget): void {
    this.friend = null;
    this.begin(t);
  }

  /** Follow a friend who lets you see them on the map. */
  trackFriend(id: string, name: string, now = performance.now()): void {
    this.friend = { id, name };
    this.current = null;
    this.askedAt = now;
    this.locate?.(id);
    this.changed();
  }

  /** Rename what is tracked, as when the parcel under a spot is learnt. */
  relabel(label: string, detail: string): void {
    if (!this.current) return;
    this.current = { ...this.current, label, detail };
    this.changed();
  }

  /**
   * The grid says where a region is. Something tracked in it was placed
   * on the square the region was on then, which a landmark keeps from the
   * day it was made: a region moved since has the beacon, the arrow and
   * the map follow it to where it is now.
   */
  regionAt(regionId: string, gridX: number, gridY: number): void {
    const t = this.current;
    if (!t || t.regionId !== regionId || (t.gridX === gridX && t.gridY === gridY)) return;
    this.current = { ...t, gridX, gridY };
    this.changed();
  }

  stop(): void {
    if (!this.current && !this.friend) return;
    this.current = null;
    this.friend = null;
    this.changed();
  }

  /** Hear where a followed friend is, and whether they went offline. */
  listen(conn: RegionConnection): () => void {
    const offs = [
      conn.on("FriendLocateReply", (m) => this.located(m)),
      conn.on("FriendPresenceUpdate", (m) => {
        if (this.friend?.id !== m.id || m.presence !== FriendPresence.Offline) return;
        this.notice?.(`${this.friend.name} has gone offline, so is no longer being tracked.`);
        this.stop();
      }),
    ];
    return () => offs.forEach((off) => off());
  }

  located(m: FriendLocateReply): void {
    const f = this.friend;
    if (!f || m.id !== f.id) return;
    if (!m.found) {
      this.notice?.(`${f.name} cannot be found on the map.`);
      this.stop();
      return;
    }
    const first = !this.current;
    this.current = {
      kind: "friend",
      regionId: m.region_id,
      regionName: m.region_name,
      gridX: m.grid_x,
      gridY: m.grid_y,
      position: { ...m.position },
      label: f.name,
      detail: m.region_name,
      id: f.id,
    };
    if (first) this.measured = false;
    this.changed();
  }

  /**
   * Where the target is from here. A spot picked on the map, or a mark
   * from it, is lifted clear of the ground when it is in this region and
   * the ground is known: one picked from the air is meant for whatever is
   * under it. A landmark is where it was made, and a friend where they are.
   */
  spot(here: Here): Vec3 | null {
    const t = this.current;
    if (!t) return null;
    const at = relativeTo(here, t);
    const placed = t.kind === "location" || t.kind === "event" || t.kind === "land";
    if (placed && t.regionId === here.regionId && here.ground) {
      at.z = Math.max(at.z, here.ground(at.x, at.y) + ABOVE_GROUND);
    }
    return at;
  }

  /**
   * Once a frame: ask after a followed friend when it is time, and put the
   * beacon down on arriving. Returns whether it was arrived at.
   */
  update(now: number, here: Here | null): boolean {
    if (this.friend && now - this.askedAt >= FRIEND_POLL_MS) {
      this.askedAt = now;
      this.locate?.(this.friend.id);
    }
    const t = this.current;
    const at = here ? this.spot(here) : null;
    if (!t || !here || !at) return false;
    const gap = arrivalGap(t.kind, here.self, at);
    if (!this.measured) {
      this.measured = true;
      this.armed = t.kind !== "landmark" || gap >= LANDMARK_NEAR;
    }
    if (!this.armed) {
      if (gap > LANDMARK_NEAR) this.armed = true;
      return false;
    }
    if (gap >= ARRIVED_WITHIN) return false;
    this.stop();
    return true;
  }

  private begin(t: TrackTarget): void {
    this.current = t;
    this.measured = false;
    this.armed = true;
    this.changed();
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }
}
